import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  clearAdminSession,
  getAdminHeaders,
  hasPermission,
  PERMS,
  validateAdminSession,
  AdminUserInfo,
} from '../../utils/adminAuth';
import './SettingsPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface AdminAccount {
  id: number;
  username: string;
  role: string;
  displayName: string;
  isActive: boolean;
  mustResetPassword: boolean;
  accessWaiverAccepted?: boolean;
  accessWaiverAcceptedAt?: string | null;
  volunteerUserId?: number | null;
  volunteerName?: string;
  volunteerEmail?: string;
  lastLoginAt?: string | null;
}

interface VolunteerHit {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  passcode: string;
  isAdmin: boolean;
}

interface GroupMember {
  id: number;
  userId: number | null;
  firstName: string;
  lastName: string;
  passcode: string;
  joinedAt: string;
  leftAt?: string | null;
}

interface GroupHit {
  id: number;
  firstName: string;
  lastName: string;
  passcode: string;
  isActive: boolean;
  inGroup: boolean;
}

const formatGroupDay = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Each supervisor tab is on/off. When on, grants view+read (+ print for Manage Volunteers). */
const SUPERVISOR_TABS: { id: string; label: string; keys: string[] }[] = [
  {
    id: 'manage_volunteers',
    label: 'Manage Volunteers',
    keys: [PERMS.manageVolunteersView, PERMS.manageVolunteersRead, PERMS.manageVolunteersPrint],
  },
  {
    id: 'emergency',
    label: 'Emergency Contacts',
    keys: [PERMS.emergencyView, PERMS.emergencyRead],
  },
  {
    id: 'liability',
    label: 'Liability Waivers',
    keys: [PERMS.liabilityView, PERMS.liabilityRead],
  },
  {
    id: 'reports_directory',
    label: 'Reports, Directory',
    keys: [PERMS.reportsDirectoryView, PERMS.reportsDirectoryRead],
  },
  {
    id: 'reports_time',
    label: 'Reports, Time',
    keys: [PERMS.reportsTimeView, PERMS.reportsTimeRead],
  },
  {
    id: 'reports_calendar',
    label: 'Reports, Calendar',
    keys: [PERMS.reportsCalendarView, PERMS.reportsCalendarRead],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    keys: [PERMS.notificationsView, PERMS.notificationsRead],
  },
  {
    id: 'activity_log',
    label: 'Activity Log',
    keys: [PERMS.activityLogView, PERMS.activityLogRead],
  },
  {
    id: 'feedback',
    label: 'Feedback',
    keys: [PERMS.feedbackView, PERMS.feedbackRead],
  },
];

const isTabEnabled = (perms: string[], keys: string[]) => keys.some((k) => perms.includes(k));

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [tier2Perms, setTier2Perms] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [passwordModal, setPasswordModal] = useState<{ id: number; username: string } | null>(null);
  const [emailModal, setEmailModal] = useState<{ kind: 'reset' | 'onboard'; id: number; username: string } | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [permsSaveState, setPermsSaveState] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');

  const [supervisorUsername, setSupervisorUsername] = useState('');
  const [supervisorPassword, setSupervisorPassword] = useState('');
  const [supervisorNameReady, setSupervisorNameReady] = useState(false);
  const [supervisorPasswordReady, setSupervisorPasswordReady] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [promoteQuery, setPromoteQuery] = useState('');
  const [promoteHits, setPromoteHits] = useState<VolunteerHit[]>([]);
  const [groupModal, setGroupModal] = useState<{ id: number; username: string } | null>(null);
  const [groupCurrent, setGroupCurrent] = useState<GroupMember[]>([]);
  const [groupQuery, setGroupQuery] = useState('');
  const [groupHits, setGroupHits] = useState<GroupHit[]>([]);
  const [groupError, setGroupError] = useState('');
  const [groupBusy, setGroupBusy] = useState(false);
  const [groupLoading, setGroupLoading] = useState(false);

  const loadData = useCallback(async () => {
    const session = await validateAdminSession();
    if (!session) {
      navigate('/admin-login');
      return;
    }
    if (session.mustResetPassword) {
      navigate('/admin/change-password');
      return;
    }
    if (!hasPermission(PERMS.settingsManageAccess, session)) {
      navigate('/admin-panel');
      return;
    }
    setUser(session);

    const [usersRes, permsRes] = await Promise.all([
      fetch(`${API_BASE_URL}/admin/users`, { headers: getAdminHeaders() }),
      fetch(`${API_BASE_URL}/admin/tier2-permissions`, { headers: getAdminHeaders() }),
    ]);
    const usersData = await usersRes.json();
    const permsData = await permsRes.json();
    if (usersRes.ok && usersData.success) setAccounts(usersData.users || []);
    if (permsRes.ok && permsData.success) setTier2Perms(permsData.permissions || []);
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showMsg = (text: string) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 4000);
  };

  const createUser = async (role: 'admin' | 'supervisor') => {
    const username = role === 'supervisor' ? supervisorUsername : adminUsername;
    const password = role === 'supervisor' ? supervisorPassword : adminPassword;
    if (!username.trim()) {
      showMsg('Username is required');
      return;
    }
    const response = await fetch(`${API_BASE_URL}/admin/users`, {
      method: 'POST',
      headers: getAdminHeaders(),
      body: JSON.stringify({ username: username.trim(), password, role, displayName: username.trim() }),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      showMsg(data.message || 'Failed to create user');
      return;
    }
    if (role === 'admin' && data.temporaryPassword) {
      window.alert(`Created admin "${data.username}". Temporary password:\n\n${data.temporaryPassword}\n\nThey must change it on first login.`);
    } else {
      showMsg(`Created supervisor "${data.username}"`);
    }
    if (role === 'supervisor') {
      setSupervisorUsername('');
      setSupervisorPassword('');
    } else {
      setAdminUsername('');
      setAdminPassword('');
    }
    loadData();
  };

  const searchPromote = async (q: string) => {
    setPromoteQuery(q);
    if (q.trim().length < 2) {
      setPromoteHits([]);
      return;
    }
    const response = await fetch(
      `${API_BASE_URL}/admin/users/search-volunteers?q=${encodeURIComponent(q.trim())}`,
      { headers: getAdminHeaders() }
    );
    const data = await response.json();
    if (response.ok && data.success) setPromoteHits(data.volunteers || []);
  };

  const promote = async (volunteerUserId: number) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/promote`, {
      method: 'POST',
      headers: getAdminHeaders(),
      body: JSON.stringify({ volunteerUserId }),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      showMsg(data.message || 'Failed to promote');
      return;
    }
    if (data.emailSent === false) {
      showMsg(`Promoted ${data.displayName}, but the onboarding email failed. Use Resend onboarding.`);
    } else {
      showMsg(`Promoted ${data.displayName}. We emailed a unique setup link to ${data.username}.`);
    }
    setPromoteQuery('');
    setPromoteHits([]);
    loadData();
  };

  const openSupervisorPasswordModal = (id: number, username: string) => {
    setPasswordModal({ id, username });
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const closePasswordModal = () => {
    setPasswordModal(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const loadGroup = async (id: number) => {
    setGroupLoading(true);
    setGroupError('');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/supervisors/${id}/group`, {
        headers: getAdminHeaders(),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setGroupError(data.message || 'Failed to load group');
        return;
      }
      setGroupCurrent(data.current || []);
    } catch {
      setGroupError('Failed to load group');
    } finally {
      setGroupLoading(false);
    }
  };

  const openGroup = (id: number, username: string) => {
    setGroupModal({ id, username });
    setGroupQuery('');
    setGroupHits([]);
    setGroupError('');
    setGroupCurrent([]);
    loadGroup(id);
  };

  const closeGroup = () => {
    setGroupModal(null);
    setGroupQuery('');
    setGroupHits([]);
    setGroupError('');
  };

  const searchGroup = async (q: string) => {
    setGroupQuery(q);
    if (!groupModal || q.trim().length < 2) {
      setGroupHits([]);
      return;
    }
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/supervisors/${groupModal.id}/group/search?q=${encodeURIComponent(q.trim())}`,
        { headers: getAdminHeaders() },
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        setGroupError(data.message || 'Search failed');
        return;
      }
      setGroupHits(data.volunteers || []);
    } catch {
      setGroupError('Search failed');
    }
  };

  const addToGroup = async (userId: number) => {
    if (!groupModal) return;
    setGroupBusy(true);
    setGroupError('');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/supervisors/${groupModal.id}/group`, {
        method: 'POST',
        headers: { ...getAdminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setGroupError(data.message || 'Failed to add person');
        return;
      }
      setGroupQuery('');
      setGroupHits([]);
      await loadGroup(groupModal.id);
    } catch {
      setGroupError('Failed to add person');
    } finally {
      setGroupBusy(false);
    }
  };

  const removeFromGroup = async (membershipId: number) => {
    if (!groupModal) return;
    setGroupBusy(true);
    setGroupError('');
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/supervisors/${groupModal.id}/group/${membershipId}`,
        { method: 'DELETE', headers: getAdminHeaders() },
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        setGroupError(data.message || 'Failed to remove person');
        return;
      }
      await loadGroup(groupModal.id);
      if (groupQuery.trim().length >= 2) {
        await searchGroup(groupQuery);
      }
    } catch {
      setGroupError('Failed to remove person');
    } finally {
      setGroupBusy(false);
    }
  };

  const saveSupervisorPassword = async () => {
    if (!passwordModal) return;
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordSaving(true);
    setPasswordError('');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${passwordModal.id}/password`, {
        method: 'PUT',
        headers: getAdminHeaders(),
        body: JSON.stringify({ password: newPassword }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setPasswordError(data.message || 'Failed to update password');
        return;
      }
      const username = passwordModal.username;
      closePasswordModal();
      showMsg(`Password updated for ${username}`);
    } catch {
      setPasswordError('Failed to connect to server');
    } finally {
      setPasswordSaving(false);
    }
  };

  const openEmailModal = (kind: 'reset' | 'onboard', id: number, username: string) => {
    setEmailModal({ kind, id, username });
    setEmailError('');
  };

  const closeEmailModal = () => {
    if (emailSending) return;
    setEmailModal(null);
    setEmailError('');
  };

  const confirmSendEmail = async () => {
    if (!emailModal) return;
    setEmailSending(true);
    setEmailError('');
    const path = emailModal.kind === 'reset'
      ? `${API_BASE_URL}/admin/users/${emailModal.id}/reset-password`
      : `${API_BASE_URL}/admin/users/${emailModal.id}/resend-invite`;
    try {
      const response = await fetch(path, {
        method: 'POST',
        headers: getAdminHeaders(),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setEmailError(data.message || 'Failed to send email');
        return;
      }
      const username = emailModal.username;
      const kind = emailModal.kind;
      setEmailModal(null);
      showMsg(
        kind === 'reset'
          ? `Password reset email sent to ${username}`
          : `Onboarding email sent to ${username}`
      );
    } catch {
      setEmailError('Failed to connect to server');
    } finally {
      setEmailSending(false);
    }
  };

  const toggleActive = async (account: AdminAccount) => {
    const response = await fetch(`${API_BASE_URL}/admin/users/${account.id}/active`, {
      method: 'PUT',
      headers: getAdminHeaders(),
      body: JSON.stringify({ isActive: !account.isActive }),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      showMsg(data.message || 'Update failed');
      return;
    }
    loadData();
  };

  const toggleTab = (keys: string[]) => {
    setTier2Perms((prev) => {
      const enabled = isTabEnabled(prev, keys);
      if (enabled) {
        return prev.filter((p) => !keys.includes(p));
      }
      // Turn on: add this tab's keys; never include write from this UI
      const next = new Set(prev.filter((p) => p !== PERMS.manageVolunteersWrite));
      keys.forEach((k) => next.add(k));
      return Array.from(next);
    });
  };

  const saveTier2Perms = async () => {
    if (permsSaveState === 'saving') return;
    setPermsSaveState('saving');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/tier2-permissions`, {
        method: 'PUT',
        headers: getAdminHeaders(),
        body: JSON.stringify({ permissions: tier2Perms }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setPermsSaveState('error');
      } else {
        setPermsSaveState('success');
      }
    } catch {
      setPermsSaveState('error');
    }
    setTimeout(() => setPermsSaveState('idle'), 2500);
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="settings-card">
          <p>Loading access settings...</p>
        </div>
      </div>
    );
  }

  const admins = accounts.filter((a) => a.role === 'admin');
  const supervisors = accounts.filter((a) => a.role === 'supervisor');
  const canManageAdmins = user?.role === 'breakglass';
  const canEditGroups = user?.role === 'admin' || user?.role === 'breakglass';

  return (
    <div className="settings-container">
      <div className="settings-card settings-card-wide">
        <div className="settings-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="settings-title">Access Control</h1>
          <p className="settings-subtitle">
            Signed in as {user?.username} ({canManageAdmins ? 'Break-glass Admin' : 'Admin'}).{' '}
            {canManageAdmins
              ? 'Manage admins, supervisors, and supervisor permissions.'
              : 'Manage supervisors and supervisor permissions.'}
          </p>
        </div>

        {message && <div className="access-banner">{message}</div>}

        <div className="access-sections">
          <section className="access-section">
            <h2>Supervisors</h2>
            <p className="access-help">Shared desk logins (e.g. front desk). Same global permissions for all supervisors.</p>
            <div className="access-inline-form">
              <input
                placeholder="Username (e.g. front desk)"
                value={supervisorUsername}
                onChange={(e) => setSupervisorUsername(e.target.value)}
                name="new-supervisor-name"
                autoComplete="off"
                readOnly={!supervisorNameReady}
                onFocus={() => setSupervisorNameReady(true)}
              />
              <input
                type="password"
                placeholder="Password (optional — generated if blank)"
                value={supervisorPassword}
                onChange={(e) => setSupervisorPassword(e.target.value)}
                name="new-supervisor-secret"
                autoComplete="new-password"
                readOnly={!supervisorPasswordReady}
                onFocus={() => setSupervisorPasswordReady(true)}
              />
              <button type="button" className="access-primary-btn" onClick={() => createUser('supervisor')}>
                Create supervisor
              </button>
            </div>
            <ul className="access-user-list">
              {supervisors.map((a) => (
                <li key={a.id}>
                  <div>
                    <strong>{a.username}</strong>
                    {!a.isActive && <span className="badge-inactive"> inactive</span>}
                  </div>
                  <div className="access-user-actions">
                    <button type="button" onClick={() => openSupervisorPasswordModal(a.id, a.username)}>
                      Change password
                    </button>
                    <button type="button" onClick={() => openGroup(a.id, a.username)}>
                      Group
                    </button>
                    <button type="button" onClick={() => toggleActive(a)}>
                      {a.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </li>
              ))}
              {supervisors.length === 0 && <li className="access-empty">No supervisors yet.</li>}
            </ul>
          </section>

          {canManageAdmins && <section className="access-section">
            <h2>Admins</h2>
            <p className="access-help">
              Promote a volunteer by email/name, or create an admin username. Promoted admins get a unique
              email link to set their password and accept the admin access waiver. They cannot use the admin
              panel until they accept it.
            </p>
            <div className="access-inline-form">
              <input
                placeholder="Search volunteers by name or email"
                value={promoteQuery}
                onChange={(e) => searchPromote(e.target.value)}
              />
            </div>
            {promoteHits.length > 0 && (
              <ul className="access-user-list">
                {promoteHits.map((v) => (
                  <li key={v.id}>
                    <div>
                      <strong>{v.firstName} {v.lastName}</strong>
                      <div className="access-meta">{v.email} · PIN {v.passcode}</div>
                    </div>
                    <div className="access-user-actions">
                      {v.isAdmin ? (
                        <span className="badge-inactive">Already admin</span>
                      ) : (
                        <button type="button" onClick={() => promote(v.id)}>Promote</button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="access-inline-form" style={{ marginTop: 16 }}>
              <input
                placeholder="New admin username / email"
                value={adminUsername}
                onChange={(e) => setAdminUsername(e.target.value)}
              />
              <input
                type="password"
                placeholder="Temp password (optional)"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
              />
              <button type="button" className="access-primary-btn" onClick={() => createUser('admin')}>
                Create admin
              </button>
            </div>
            <ul className="access-user-list">
              {admins.map((a) => (
                <li key={a.id}>
                  <div className="access-user-main">
                    <span
                      className={`waiver-status-icon ${a.accessWaiverAccepted ? 'accepted' : 'pending'}`}
                      title={
                        a.accessWaiverAccepted
                          ? 'Admin access waiver accepted'
                          : 'Admin access waiver not accepted — cannot sign in yet'
                      }
                      aria-label={
                        a.accessWaiverAccepted
                          ? 'Admin access waiver accepted'
                          : 'Admin access waiver not accepted'
                      }
                    >
                      {a.accessWaiverAccepted ? (
                        <svg viewBox="0 0 20 20" aria-hidden="true">
                          <path
                            fill="currentColor"
                            d="M10 1.5A8.5 8.5 0 1 0 18.5 10 8.51 8.51 0 0 0 10 1.5Zm4.06 6.16-4.4 5.5a.75.75 0 0 1-1.14.04l-2.5-2.6a.75.75 0 1 1 1.08-1.04l1.9 1.97 3.86-4.83a.75.75 0 1 1 1.2.96Z"
                          />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 20 20" aria-hidden="true">
                          <path
                            fill="currentColor"
                            d="M10 1.5A8.5 8.5 0 1 0 18.5 10 8.51 8.51 0 0 0 10 1.5ZM9.25 6.5a.75.75 0 0 1 1.5 0v4a.75.75 0 0 1-1.5 0Zm.75 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
                          />
                        </svg>
                      )}
                    </span>
                    <div>
                      <strong>{a.displayName || a.username}</strong>
                      {!a.isActive && <span className="badge-inactive"> inactive</span>}
                      <div className="access-meta">
                        {a.volunteerEmail || a.username}
                        {!a.accessWaiverAccepted ? ' · access waiver pending' : ''}
                      </div>
                    </div>
                  </div>
                  <div className="access-user-actions">
                    {a.accessWaiverAccepted ? (
                      <button type="button" onClick={() => openEmailModal('reset', a.id, a.username)}>
                        Reset password
                      </button>
                    ) : (
                      <button type="button" onClick={() => openEmailModal('onboard', a.id, a.username)}>
                        Resend onboarding
                      </button>
                    )}
                    <button type="button" onClick={() => toggleActive(a)}>
                      {a.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </li>
              ))}
              {admins.length === 0 && <li className="access-empty">No DB admin accounts yet (break-glass env admin still works).</li>}
            </ul>
          </section>}

          <section className="access-section">
            <h2>Supervisors Global Permissions</h2>
            <p className="access-help">
              Tap a tab to turn it on (green) or off for all supervisors. On includes view and read; Manage Volunteers also includes print badge.
            </p>
            <div className="supervisor-tab-list">
              {SUPERVISOR_TABS.map((tab) => {
                const active = isTabEnabled(tier2Perms, tab.keys);
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`supervisor-tab-item ${active ? 'active' : ''}`}
                    onClick={() => toggleTab(tab.keys)}
                    aria-pressed={active}
                  >
                    <span className="supervisor-tab-label">{tab.label}</span>
                    <span className="supervisor-tab-state">{active ? 'On' : 'Off'}</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className={`access-primary-btn perms-save-btn ${
                permsSaveState === 'success' ? 'perms-save-success' : ''
              } ${permsSaveState === 'error' ? 'perms-save-error' : ''}`}
              onClick={saveTier2Perms}
              disabled={permsSaveState === 'saving'}
            >
              {permsSaveState === 'saving' && 'Saving...'}
              {permsSaveState === 'success' && 'Successful — permissions updated'}
              {permsSaveState === 'error' && 'Not Successful'}
              {permsSaveState === 'idle' && 'Save Supervisors Global Permissions'}
            </button>
          </section>
        </div>

        <div className="settings-footer">
          <button onClick={() => navigate('/admin-panel')} className="back-button">
            ← Back to Admin Panel
          </button>
          <button
            onClick={() => {
              clearAdminSession();
              navigate('/admin-login');
            }}
            className="back-button"
          >
            Logout
          </button>
        </div>
      </div>

      {groupModal && (
        <div className="access-modal-overlay" onClick={closeGroup}>
          <div className="access-modal group-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{groupModal.username} group</h3>
            <p className="access-help">
              {canEditGroups
                ? 'Add volunteers by name or user code. They stay on their volunteer account.'
                : 'Volunteers in this group stay on their volunteer account.'}
            </p>
            {groupError && <div className="access-error">{groupError}</div>}
            {canEditGroups && (
            <label>
              Find a volunteer
              <input
                placeholder="Name or user code"
                value={groupQuery}
                onChange={(e) => searchGroup(e.target.value)}
                disabled={groupBusy}
                autoFocus
              />
            </label>
            )}
            {canEditGroups && groupHits.length > 0 && (
              <ul className="access-user-list group-results">
                {groupHits.map((v) => (
                  <li key={v.id}>
                    <div>
                      <strong>{v.firstName} {v.lastName}</strong>
                      <div className="access-meta">
                        User code {v.passcode}
                        {!v.isActive && ' · inactive'}
                      </div>
                    </div>
                    {v.inGroup ? (
                      <span className="access-meta">In group</span>
                    ) : (
                      <div className="access-user-actions">
                        <button type="button" onClick={() => addToGroup(v.id)} disabled={groupBusy}>
                          Add
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <h4>In this group</h4>
            {groupLoading ? (
              <p className="access-empty">Loading...</p>
            ) : groupCurrent.length === 0 ? (
              <p className="access-empty">No one is in this group yet.</p>
            ) : (
              <ul className="access-user-list">
                {groupCurrent.map((m) => (
                  <li key={m.id}>
                    <div>
                      <strong>{m.firstName} {m.lastName}</strong>
                      <div className="access-meta">Added {formatGroupDay(m.joinedAt)}</div>
                    </div>
                    {canEditGroups && (
                      <div className="access-user-actions">
                        <button type="button" onClick={() => removeFromGroup(m.id)} disabled={groupBusy}>
                          Remove
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <div className="access-modal-actions">
              <button type="button" className="back-button" onClick={closeGroup}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {passwordModal && (
        <div className="access-modal-overlay" onClick={closePasswordModal}>
          <div className="access-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Change password</h3>
            <p className="access-help">Set a new password for <strong>{passwordModal.username}</strong>.</p>
            {passwordError && <div className="access-error">{passwordError}</div>}
            <label>
              New password
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={passwordSaving}
                autoFocus
              />
            </label>
            <label>
              Confirm password
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={passwordSaving}
              />
            </label>
            <div className="access-modal-actions">
              <button type="button" className="back-button" onClick={closePasswordModal} disabled={passwordSaving}>
                Cancel
              </button>
              <button type="button" className="access-primary-btn" onClick={saveSupervisorPassword} disabled={passwordSaving}>
                {passwordSaving ? 'Saving...' : 'Save password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {emailModal && (
        <div className="access-modal-overlay" onClick={closeEmailModal}>
          <div className="access-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{emailModal.kind === 'reset' ? 'Send password reset' : 'Resend onboarding'}</h3>
            <p className="access-help">
              {emailModal.kind === 'reset' ? (
                <>
                  Send a password reset email to <strong>{emailModal.username}</strong>? They will get a unique
                  link to choose a new password.
                </>
              ) : (
                <>
                  Send the onboarding email to <strong>{emailModal.username}</strong>? They will get a unique
                  link to set a password and accept the admin access waiver.
                </>
              )}
            </p>
            {emailError && <div className="access-error">{emailError}</div>}
            <div className="access-modal-actions">
              <button type="button" className="back-button" onClick={closeEmailModal} disabled={emailSending}>
                Cancel
              </button>
              <button type="button" className="access-primary-btn" onClick={confirmSendEmail} disabled={emailSending}>
                {emailSending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
